import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Button, Link, useRecipe, useSlotRecipe} from '@chakra-ui/react3'
import ctaSlotRecipe from './index.recipe'
import Icon from '../icon'
import useCTAAction from '../../../hooks/useCtaAction'
import {normalizeString} from '../../../utils/utils'

/**
 * Call-to-Action button component for the Experience Leap CMS.
 * Supports primary, secondary, and text variants with accessibility features.
 * Intended for use within the Leap CMS modules.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {string} props.label - The text content to display on the button.
 * @param {('primary'|'secondary'|'text')} [props.variant='primary'] - Button style variant.
 * @param {string} [props.screenReader] - Accessibility label for screen readers (aria-label).
 * @param {Object} [props.action] - Action object with type and value properties.
 * @param {('category'|'content'|'product'|'url'|'anchor'|'overlay'|'sidebar')} [props.action.type] - Type of action to perform.
 * @param {string} [props.action.value] - The value for the action (URL, product ID, etc.).
 * @param {boolean} [props.openInNewTab=false] - Opens the link in a new tab when true.
 * @param {function} [props.onClick] - Optional click handler function. Can prevent default handling
 * @returns {JSX.Element} Rendered CTA button element.
 */
const CTA = ({
    label,
    icon,
    iconPosition,
    variant = 'primary',
    screenReader,
    action,
    openInNewTab = false,
    onClick: _onClick,
    trackingContext: {moduleName, eventLabel} = {},
    ...rest
}) => {
    const ctaProps = useCTAAction({
        action,
        openInNewTab,
        onClick: _onClick,
        trackingContext: {moduleName, eventLabel}
    })
    // use slot recipe to test the approach, altough in this case a simple useRecipe would be sufficient
    const recipe = useSlotRecipe({recipe: ctaSlotRecipe})
    // chakra recommends to use recipe.splitVariantProps(props) to split props and parent styles - drawback is function interface becomes less clean
    // https://chakra-ui.com/docs/theming/recipes#splitvariantprops
    // for now keep it simple as the recipe is internal for the component
    const styles = useMemo(() => recipe({variant}), [recipe, variant])
    // get the button recipe to split the button props and styles
    const buttonRecipe = useRecipe({key: 'button'})
    const [buttonProps, buttonStyles] = useMemo(
        () => buttonRecipe.splitVariantProps(styles.button),
        [styles.button, buttonRecipe]
    )
    // get the link recipe to split the link props and styles
    const linkRecipe = useRecipe({key: 'link'})
    const [linkProps, linkStyles] = useMemo(
        () => linkRecipe.splitVariantProps(styles.link),
        [styles.link, linkRecipe]
    )
    const ariaLabel = normalizeString(screenReader) || undefined

    // do not render if no label is provided
    if (!label) {
        return null
    }
    // Use Link component for text variants
    if (/^text/.test(variant)) {
        return (
            <Link {...linkProps} {...ctaProps} css={linkStyles} aria-label={ariaLabel} {...rest}>
                {iconPosition === 'left' && <Icon {...icon} />}
                <span>{label}</span>
                {iconPosition === 'right' && <Icon {...icon} />}
            </Link>
        )
    }

    return (
        <Button
            // button props are spread
            {...buttonProps}
            // button styles are applied vie css prop
            css={buttonStyles}
            aria-label={ariaLabel}
            {...ctaProps}
            {...rest}
        >
            {iconPosition === 'left' && <Icon {...icon} />}
            <span>{label}</span>
            {iconPosition === 'right' && <Icon {...icon} />}
        </Button>
    )
}

CTA.propTypes = {
    label: PropTypes.string.isRequired,
    icon: PropTypes.object,
    iconPosition: PropTypes.oneOf(['left', 'right']),
    variant: PropTypes.oneOf(['primary', 'secondary', 'text']),
    screenReader: PropTypes.string,
    action: PropTypes.shape({
        type: PropTypes.oneOf([
            'category',
            'content',
            'product',
            'url',
            'anchor',
            'overlay',
            'sidebar'
        ]),
        value: PropTypes.string
    }),
    openInNewTab: PropTypes.bool,
    onClick: PropTypes.func,
    trackingContext: PropTypes.shape({
        moduleName: PropTypes.string,
        eventLabel: PropTypes.string
    })
}

export default CTA
